import React from "react";
import * as Mui from "@mui/material";
import { ArrowForward, HistoryOutlined } from "@mui/icons-material";
import moment from "moment";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import {
  designColors,
  designRadii,
  designType,
  designWeights,
} from "services/constants/designTokens";

// Audit Trail tab of a Pay Inputs view panel (from the mockup): one card
// per entry, newest first — who made the change and when, and each changed
// field as "Label: from -> to".

const AVATAR_TINTS = [
  { bg: "#EAF2FF", fg: "#2878FF" },
  { bg: "#F1EDFF", fg: "#6D5DF6" },
  { bg: "#FDF2F8", fg: "#DB2777" },
  { bg: "#E7F8EF", fg: "#12B76A" },
  { bg: "#FFF4E5", fg: "#D97706" },
];

const tintFor = (name) => {
  const sum = [...String(name)].reduce((total, char) => total + char.charCodeAt(0), 0);
  return AVATAR_TINTS[sum % AVATAR_TINTS.length];
};

const initials = (name) =>
  String(name)
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const ChangeLine = ({ change }) => (
  <Mui.Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      columnGap: 1,
      rowGap: 0.5,
      px: 1,
      py: 0.75,
      borderRadius: designRadii.control,
      backgroundColor: designColors.slateBg,
    }}
  >
    <Mui.Box
      component="span"
      sx={{
        px: 1,
        py: 0.25,
        borderRadius: 1,
        backgroundColor: "#FFF4E5",
        color: "#92400E",
        fontSize: 12.5,
        fontWeight: designWeights.medium,
        minWidth: 0,
        overflowWrap: "anywhere",
      }}
    >
      {change.label}:
    </Mui.Box>
    {change.value !== undefined ? null : (
      <>
        <Mui.Typography sx={{ ...designType.helperText, fontSize: 13, color: designColors.slate900, overflowWrap: "anywhere", fontVariantNumeric: "tabular-nums", minWidth: 0 }}>
          {change.from}
        </Mui.Typography>
        <ArrowForward sx={{ fontSize: 15, color: "#000000", flexShrink: 0 }} />
      </>
    )}
    <Mui.Typography
      sx={{
        fontSize: 13,
        fontWeight: designWeights.semibold,
        color: designColors.slate900,
        overflowWrap: "anywhere",
        fontVariantNumeric: "tabular-nums",
        minWidth: 0,
      }}
    >
      {change.value !== undefined ? change.value : change.to}
    </Mui.Typography>
  </Mui.Box>
);

// Optional small tags beside an entry title (e.g. "Current").
const TAG_TONES = {
  green: { bg: "#E7F8EF", fg: "#067647" },
  slate: { bg: designColors.slate100, fg: designColors.slate600 },
  blue: { bg: "#EAF2FF", fg: "#1D4ED8" },
};
const EntryTag = ({ tag }) => {
  const tone = TAG_TONES[tag.tone] || TAG_TONES.slate;
  return (
    <Mui.Box
      component="span"
      sx={{ px: 1, py: 0.25, borderRadius: 1, fontSize: 12, fontWeight: designWeights.semibold, backgroundColor: tone.bg, color: tone.fg, whiteSpace: "nowrap" }}
    >
      {tag.label}
    </Mui.Box>
  );
};

const AuditCard = ({ entry }) => {
  const tint = tintFor(entry.actor?.name);
  return (
    <Mui.Box sx={{ ...detailCardSx, p: 1.75 }}>
      <Mui.Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Mui.Avatar
          sx={{
            width: 36,
            height: 36,
            fontSize: 13,
            fontWeight: designWeights.semibold,
            backgroundColor: tint.bg,
            color: tint.fg,
          }}
        >
          {initials(entry.actor?.name)}
        </Mui.Avatar>
        <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
          <Mui.Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            columnGap={1}
          >
            <Mui.Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>
                {entry.title}
              </Mui.Typography>
              {(entry.tags || []).map((tag) => <EntryTag key={tag.label} tag={tag} />)}
            </Mui.Stack>
            <Mui.Typography sx={{ ...designType.captionText, whiteSpace: "nowrap" }}>
              {moment(entry.at).format("DD MMM YYYY, hh:mm A")}
            </Mui.Typography>
          </Mui.Stack>
          <Mui.Typography sx={designType.captionText}>
            {[entry.actor?.name, entry.actor?.role].filter(Boolean).join(" · ")}
          </Mui.Typography>
        </Mui.Box>
      </Mui.Stack>
      {entry.changes.length || entry.details?.length || entry.note ? (
        <Mui.Stack spacing={0.75} sx={{ mt: 1.25 }}>
          {entry.changes.map((change) => (
            <ChangeLine key={change.label} change={change} />
          ))}
          {(entry.details || []).map((detail) => (
            <ChangeLine key={`detail-${detail.label}`} change={detail} />
          ))}
          {entry.note ? (
            <Mui.Typography
              sx={{
                ...designType.helperText,
                fontSize: 13,
                px: 1,
                py: 0.75,
                borderRadius: designRadii.control,
                backgroundColor: designColors.slateBg,
              }}
            >
              {entry.note}
            </Mui.Typography>
          ) : null}
        </Mui.Stack>
      ) : null}
    </Mui.Box>
  );
};

// entries: newest first, in the shape built by auditHelpers.js. Optional
// per entry: tags [{ label, tone }] beside the title, and details
// [{ label, value }] shown as "Label: value" lines after the changes.
export const PayInputAuditTrail = ({ entries = [] }) => (
  <Mui.Stack spacing={1.5}>
    {entries.length ? (
      entries.map((entry) => <AuditCard key={entry.id} entry={entry} />)
    ) : (
      <Mui.Stack alignItems="center" spacing={1} sx={{ ...detailCardSx, py: 4 }}>
        <HistoryOutlined sx={{ fontSize: 32, color: designColors.slate400 }} />
        <Mui.Typography sx={designType.helperText}>No changes to show.</Mui.Typography>
      </Mui.Stack>
    )}
  </Mui.Stack>
);
